import { EventDto } from '../services/api/sharedTypes/Event'
import {
    ProductDto,
    ProductOfEventDto,
    ProductStatisticsDto,
    ProductType,
    ProductWithStatisticsDto,
} from '../services/api/sharedTypes/Product'

export interface ProductQuantities {
    productId: string
    productName: string
    usedQuantity: number
    totalQuantity: number
}

export const getEmptyProductObject: () => ProductDto = () => {
    return {
        id: '0',
        name: '',
        canBeAssigned: true,
        createdAt: new Date(),
        lastModifiedAt: new Date(),
        note: '',
        productType: ProductType.Internal,
        totalQuantity: 1,
        yearOfPurchased: new Date().getFullYear(),
    }
}

export const getEmptyProductStatistics: () => ProductStatisticsDto = () => {
    return {
        assignedQuantity: 0,
        costs: 0,
        netProfitPerEvent: 0,
        netProfitPerHour: 0,
        netProfitPerPeriod: 0,
        operatingTime: '0',
        profit: 0,
        revenue: 0,
    }
}

export const convertStringToProductType = (data: number) => {
    if (data === ProductType.External) return ProductType.External
    if (data === ProductType.Internal) return ProductType.Internal
    return ProductType.NotSet
}

export const convertProductWithStatisticsDtoToProductOfEventDto = (product: ProductWithStatisticsDto): ProductOfEventDto => {
    return {
        costs: product.statistics.costs,
        isConfirmed: false,
        operatingTime: product.statistics.operatingTime,
        productId: product.id,
        productName: product.name,
        quantity: 1,
        revenue: product.statistics.revenue,
    }
}

export const convertProductOfEventDtoToProductWithStatisticsDto = (product: ProductOfEventDto): ProductWithStatisticsDto => {
    return {
        assignedEvents: [],
        statistics: getEmptyProductStatistics(),
        productType: ProductType.Internal,
        name: product.productName,
        yearOfPurchased: 0,
        totalQuantity: 0,
        note: '',
        canBeAssigned: true,
        id: product.productId,
        createdAt: new Date(),
        lastModifiedAt: new Date(),
    }
}

export enum ProductTab {
    All = 'all',
    Internal = 'internal',
    External = 'external',
}

export const getProductTab = (tab: string) => {
    if (tab === ProductTab.All) return ProductTab.All
    if (tab === ProductTab.Internal) return ProductTab.Internal
    if (tab === ProductTab.External) return ProductTab.External
    return ProductTab.All
}

export const getProductTabNumber = (tab: ProductTab) => {
    if (tab === ProductTab.All) return 0
    if (tab === ProductTab.Internal) return 1
    if (tab === ProductTab.External) return 2
    return 0
}

export const convertNumberToProductTab = (data: number) => {
    if (data === 0) return ProductTab.All
    if (data === 1) return ProductTab.Internal
    if (data === 2) return ProductTab.External
    return ProductTab.All
}

export enum ProductQuantityErrorType {
    Overused = 'overused',
    UsedElsewhere = 'usedElsewhere',
}

export const convertProductQuantityErrorTypeToString = (errorType: ProductQuantityErrorType) => {
    if (errorType === ProductQuantityErrorType.Overused) return ' - počet kusů atrakce přesahuje dovolené množství.'
    if (errorType === ProductQuantityErrorType.UsedElsewhere) return ' je ve stejné časové období přiřazena na jinou událost.'
    return ''
}

/**
 * gets quantities from productQuantities state and returns array of names of products that are used more than they are available
 * It is used to show warning under products table
 * @returns array of product names and error type
 */
export const getUsedProducts = (
    event: EventDto | undefined,
    productQuantities: ProductQuantities[]
): { productName: string; errorType: ProductQuantityErrorType }[] => {
    const usedProducts: { productName: string; errorType: ProductQuantityErrorType }[] = []
    if (!event) return usedProducts
    event.products.forEach((product) => {
        if (!product.productId || product.quantity <= 0) return usedProducts
        const usedProduct = productQuantities.find((item) => item.productId === product.productId)
        if (!usedProduct) return usedProducts
        if (usedProduct.totalQuantity < product.quantity) {
            usedProducts.push({ productName: usedProduct.productName, errorType: ProductQuantityErrorType.Overused })
        } else if (usedProduct.totalQuantity < usedProduct.usedQuantity + product.quantity) {
            usedProducts.push({ productName: usedProduct.productName, errorType: ProductQuantityErrorType.UsedElsewhere })
        }
    })
    return usedProducts
}
